import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import { StatusBadge, LoadingSpinner, Modal } from '../../components/UI';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { usePermissions } from '../../hooks/usePermissions';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://cmsapi3.vdavns.com/api').replace('/api', '');

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useSelector(s => s.auth);
  const { canApprove, canReject, isFullAccess } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null); // 'approve' | 'reject' | 'objection'
  const [remarks, setRemarks] = useState('');
  const [nocPdf, setNocPdf] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Admin/Developer/Head = view only
  const isViewOnly = ['admin', 'developer', 'head', 'HD'].includes(role);

  const load = () => {
    setLoading(true);
    api.get(`/applications/${id}`)
      .then(r => { if (r && r.application) setData(r); else setData(null); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const handleAction = async () => {
    if (!remarks && actionModal !== 'approve') {
      return toast.error('Please provide remarks');
    }
    setSubmitting(true);
    try {
      const endpoint = role === 'final_dept' ? `/final/applications/${id}/action` : `/dept/applications/${id}/action`;
      const fd = new FormData();
      fd.append('action', actionModal);
      fd.append('remarks', remarks);
      if (nocPdf) fd.append('file', nocPdf);

      await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Application ${actionModal}d successfully`);
      setActionModal(null);
      navigate(role === 'final_dept' ? '/dept/applications' : '/dept/applications');
    } catch { toast.error('Action failed'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <LoadingSpinner size="lg" className="py-20" />;
  if (!data) return <div className="text-center py-20 text-gray-500">Application not found</div>;

  const { application, documents, workflow } = data;
  const isFinal = role === 'final_dept';
  const isRelated = role === 'related_dept';

  // ── Determine if this dept can act on this application ────────
  const CLOSED = ['approved_final','rejected_final','rejected_by_related','pulled'];

  const canAct = (() => {
    // Closed applications — no one can act
    if (CLOSED.includes(application.status)) return false;

    if (isFinal) {
      // Final dept can act when:
      // 1. approved_by_related — forwarded from related dept (multi-dept flow)
      // 2. submitted — directly assigned (single dept flow: User → Final only)
      // 3. objection_by_final — resubmitted after final dept objection
      return ['approved_by_related', 'submitted', 'under_review', 'objection_by_final'].includes(application.status);
    }

    if (isRelated) {
      // Related dept can act when:
      // - submitted (new application)
      // - under_review
      // - objection_by_related (resubmitted after objection)
      return ['submitted', 'under_review', 'objection_by_related'].includes(application.status);
    }

    return false;
  })();

  const actionColors = { approve: 'btn-success', reject: 'btn-danger', objection: 'btn-warning' };
  const actionLabels = {
    approve: isFinal ? '✅ Final Approve' : '✅ Approve & Forward',
    reject: '❌ Reject',
    objection: '⚠️ Send Objection'
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-xl font-bold text-gray-800">{application.noc_name}</h1>
              <StatusBadge status={application.status} />
            </div>
            <p className="text-sm text-gray-500 font-mono">{application.application_no}</p>
            <p className="text-xs text-gray-400 mt-1">
              Submitted: {application.submitted_at ? format(new Date(application.submitted_at), 'dd MMM yyyy, hh:mm a') : 'N/A'}
            </p>
          </div>
          {/* Action Buttons — only show when dept can act */}
          {canAct && (
            <div className="flex flex-wrap gap-2">
              {(isViewOnly ? [] : ['approve', 'reject', 'objection']).map(action => (
                <button key={action} onClick={() => { setActionModal(action); setRemarks(''); setNocPdf(null); }}
                  className={`${actionColors[action]} text-sm`}>
                  {actionLabels[action]}
                </button>
              ))}
              {isViewOnly && (
                <span className="text-xs px-3 py-1.5 bg-gray-100 text-gray-500 rounded-xl">👁️ View Only</span>
              )}
            </div>
          )}
          {!canAct && (
            <div className="text-xs px-3 py-2 rounded-xl border font-medium bg-gray-50 border-gray-200 text-gray-400">
              {application.status === 'approved_final'
                ? '🏆 NOC Approved — Completed'
                : application.status === 'rejected_final' || application.status === 'rejected_by_related'
                  ? '❌ Application Rejected — Closed'
                  : application.status === 'pulled'
                    ? '🚫 Application Withdrawn by Citizen'
                    : application.status === 'approved_by_related' && isRelated
                      ? '✅ Forwarded to Final Dept — Your Action Done'
                      : application.status === 'submitted' && isFinal
                        ? '⏳ Processing...'
                        : application.status === 'objection_by_final' || application.status === 'objection_by_related'
                          ? '⏳ Waiting for Citizen Resubmission'
                          : '🔒 No Action Required'}
            </div>
          )}
        </div>
      </div>

      {/* NOC Certificate — show when approved */}
      {application.status === 'approved_final' && application.final_noc_pdf &&
        application.final_noc_pdf.endsWith('.pdf') && (
        <div className="card border-2 border-green-300 bg-green-50">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl">🏆</div>
              <div>
                <p className="font-bold text-green-800">NOC Certificate Approved</p>
                <p className="text-sm text-green-600">Click to view or download the approved NOC PDF</p>
              </div>
            </div>
            <a href={`${API_BASE}${application.final_noc_pdf}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-all shadow-md text-sm">
              📄 Download NOC Certificate
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Applicant Info */}
        <div className="card lg:col-span-2">
          <h2 className="font-semibold text-gray-800 mb-4">Applicant Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ['Applicant Name', application.applicant_name],
              ['Mobile', application.applicant_mobile],
              ['Email', application.applicant_email],
              ['Address', application.applicant_address],
              ['Description', application.description],
              ['Latitude', application.latitude],
              ['Longitude', application.longitude],
            ].map(([label, value]) => value && (
              <div key={label} className={label === 'Address' || label === 'Description' ? 'sm:col-span-2' : ''}>
                <p className="text-xs text-gray-400 mb-0.5">{label}</p>
                <p className="text-sm font-medium text-gray-800">{value}</p>
              </div>
            ))}
          </div>
          {application.latitude && application.longitude && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm">
              <span className="text-blue-600">📍 </span>
              <a href={`https://www.google.com/maps?q=${application.latitude},${application.longitude}`}
                target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                View on Google Maps
              </a>
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="card">
          <h2 className="font-semibold text-gray-800 mb-4">Documents ({documents.length})</h2>
          <div className="space-y-2">
            {documents.length === 0 ? <p className="text-sm text-gray-400">No documents</p> : (
              documents.map(doc => (
                <div key={doc.id} className="p-2.5 border border-gray-100 rounded-lg hover:border-blue-100 transition-all">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{doc.file_type?.includes('pdf') ? '📄' : '🖼️'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 truncate">{doc.document_name}</p>
                      <p className="text-xs text-gray-400">{doc.file_type}</p>
                    </div>
                    {doc.file_path && !doc.file_path.match(/\.(html|htm|js|php)$/i) ? (
                      <button
                        onClick={() => window.open(`${API_BASE}${doc.file_path}`, '_blank', 'noopener,noreferrer')}
                        className="text-blue-500 hover:text-blue-700 text-xs flex-shrink-0 underline">
                        ↗ View
                      </button>
                    ) : doc.file_path?.match(/\.(html|htm)$/i) ? (
                      <span className="text-red-400 text-xs">⚠️ Invalid file</span>
                    ) : null}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <select className="text-xs border border-gray-200 rounded px-2 py-1 flex-1"
                      value={doc.doc_status || 'uploaded'}
                      onChange={async e => {
                        try {
                          await api.put(`/documents/${doc.id}/status`, { doc_status: e.target.value, status_remark: '' });
                          toast.success('Status updated'); load();
                        } catch { toast.error('Failed'); }
                      }}>
                      <option value="uploaded">Uploaded</option>
                      <option value="pending_review">Pending Review</option>
                      <option value="verified">Verified</option>
                      <option value="objection_raised">Objection Raised</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Workflow */}
      <div className="card">
        <h2 className="font-semibold text-gray-800 mb-6">Application Timeline</h2>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
          <div className="space-y-5">
            {workflow.map((w) => {
              const icons = { submitted:'📝', forwarded:'➡️', approved:'✅', rejected:'❌', objection:'⚠️', resubmitted:'🔄', final_approved:'🏆', final_rejected:'❌', final_objection:'⚠️', pulled:'🚫' };
              return (
                <div key={w.id} className="flex items-start gap-4 relative">
                  <div className="relative z-10 w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center text-sm flex-shrink-0">
                    {icons[w.action] || '•'}
                  </div>
                  <div className="flex-1 pb-1">
                    <div className="flex justify-between items-center flex-wrap gap-1">
                      <p className="font-medium text-sm text-gray-800 capitalize">{w.action.replace(/_/g,' ')}</p>
                      <p className="text-xs text-gray-400">{format(new Date(w.created_at), 'dd MMM yyyy, hh:mm a')}</p>
                    </div>
                    {w.actor_name && <p className="text-xs text-gray-500">By: {w.actor_name}</p>}
                    {w.remarks && (
                      <div className="mt-1.5 p-2 bg-gray-50 border border-gray-100 rounded-lg">
                        <p className="text-xs text-gray-600 italic">"{w.remarks}"</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Action Modal */}
      <Modal isOpen={!!actionModal} onClose={() => setActionModal(null)}
        title={actionModal ? actionLabels[actionModal] : ''} size="md">
        <div className="space-y-4">
          {actionModal === 'approve' && isFinal && (
            <div>
              <label className="form-label">Upload Final NOC PDF (optional)</label>
              <input type="file" accept=".pdf" className="form-input"
                onChange={e => setNocPdf(e.target.files[0])} />
            </div>
          )}
          <div>
            <label className="form-label">
              Remarks{actionModal !== 'approve' && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea className="form-input" rows={4} value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder={
                actionModal === 'approve' ? 'Optional remarks...' :
                actionModal === 'reject' ? 'Reason for rejection...' :
                'Describe the objection / query...'
              } />
          </div>
          {actionModal === 'approve' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {isFinal
                ? '✅ This will finally approve the NOC and notify the citizen.'
                : '✅ This will approve and forward to the Final Approval Department.'}
            </div>
          )}
          {actionModal === 'reject' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              ❌ This will reject the application and notify the citizen.
            </div>
          )}
          {actionModal === 'objection' && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
              ⚠️ This will send the application back to the citizen for correction.
            </div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <button className="btn-secondary" onClick={() => setActionModal(null)}>Cancel</button>
            <button className={actionColors[actionModal] || 'btn-primary'} onClick={handleAction} disabled={submitting}>
              {submitting ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}